import React, { cloneElement } from "react";
import { Calendar, DateLocalizer, Event } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { CALENDER_COLORS, UNAVAILABLE_DAYS } from "lib/constants";
import { getPropsAsArrayFromArray } from "lib/utils/array-utils";
import { areDatesEqual } from "lib/utils/date-utils";

let navigate = {
  PREVIOUS: "PREV",
  NEXT: "NEXT",
  TODAY: "TODAY",
  DATE: "DATE",
};

const ViewNamesGroup = ({ views: viewNames, view, messages, onView }) => {
  return viewNames.slice(0, 3).map((name: string) => (
    <button
      type="button"
      key={name}
      className={view === name ? "rbc-active" : ""}
      onClick={() => onView(name)}
    >
      {messages[name]}
    </button>
  ));
};

const CustomToolbar = ({
  label,
  localizer: { messages },
  onNavigate,
  onView,
  view,
  views,
}) => {
  return (
    <div className="rbc-toolbar">
      <span className={"rbc-btn-group examples--custom-toolbar"}>
        <button
          type="button"
          onClick={() => onNavigate(navigate.PREVIOUS)}
          aria-label={messages.previous}
        >
          <FaArrowLeft />
        </button>
        <button
          type="button"
          onClick={() => onNavigate(navigate.TODAY)}
          aria-label={messages.today}
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => onNavigate(navigate.NEXT)}
          aria-label={messages.next}
        >
          <FaArrowRight />
        </button>
      </span>

      <span className="rbc-toolbar-label">{label}</span>

      <span className="rbc-btn-group">
        <ViewNamesGroup
          view={view}
          views={views}
          messages={messages}
          onView={onView}
        />
      </span>
    </div>
  );
};

interface PatientCalendarProps {
  localizer: DateLocalizer;
  showAppointments: boolean;
  showSessionBookings: boolean;
  appointmentList?: Event[];
  sessionBookingList?: Event[];
  onEventClick?: (event: Event) => void; // ← parent callback
}

const getEventWrapperClassName = (event: Event) => {
  return event?.resource?.type ?? "";
};

const PatientCalendar: React.FC<PatientCalendarProps> = ({
  localizer,
  showAppointments,
  showSessionBookings,
  appointmentList = [],
  sessionBookingList = [],
  onEventClick,
}) => {

  const handleEventClick = (event: Event) => {
    if (onEventClick) onEventClick(event); // ← pass event to parent
  };

  const dayPropGetter = (date: Date) => {
    if (
      showAppointments &&
      getPropsAsArrayFromArray(appointmentList, "start")?.some((d) =>
        areDatesEqual(d, date)
      )
    ) {
      return { style: { backgroundColor: CALENDER_COLORS.HOLIDAY } };
    } else if (
      showSessionBookings &&
      getPropsAsArrayFromArray(sessionBookingList, "start")?.some((d) =>
        areDatesEqual(d, date)
      )
    ) {
      return { style: { backgroundColor: CALENDER_COLORS.UNAVAILABLE } };
    }

    if (areDatesEqual(date, new Date())) {
      return { style: { backgroundColor: CALENDER_COLORS.TODAY } };
    }

    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    return {
      style: {
        backgroundColor: isWeekend
          ? CALENDER_COLORS.WEEKEND
          : CALENDER_COLORS.DEFAULT,
      },
    };
  };

  const LegendItem = ({ color, label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 28,
          height: 14,
          backgroundColor: color,
          border: "1px solid #999",
          borderRadius: 4,
        }}
      ></div>
      <span style={{ fontSize: 14 }}>{label}</span>
    </div>
  );

  return (
    <>
    <div style={{ marginTop: 8, display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 16 }}>
      <LegendItem color="#FFEDD5" label="Practitioner Appointment Booking" />
      <LegendItem color="#DCFCE7" label="Therapy Session Booking" />
    </div>
    <Calendar
      className="w-100"
      localizer={localizer}
      startAccessor="start"
      endAccessor="end"
      events={[...appointmentList, ...sessionBookingList]}
      onSelectEvent={handleEventClick} // ← CLICK HANDLER
      style={{ height: 600 }}
      min={new Date(2024, 0, 1, 8, 0, 0)}
      max={new Date(2024, 0, 1, 20, 0, 0)}
      components={{
        toolbar: CustomToolbar,

        eventWrapper: ({ event, children }: any) => {
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (onEventClick) onEventClick(event); // open modal
          };

          const updatedChild = cloneElement(children, {
            className: `${children.props.className} ${getEventWrapperClassName(event)}`,
            style: { ...children.props.style, fontSize: "12px" },
            onClick: (e: React.MouseEvent) => {
              if (typeof children.props.onClick === "function") {
                children.props.onClick(e);
              }
              handleClick(e);
            },
          });

          return (
            <div onClick={(e) => e.stopPropagation()}>
              {updatedChild}

              {event?.resource?.type === UNAVAILABLE_DAYS.PRACTITIONER_BOOKINGS && (
                <div
                  style={{
                    fontSize: "10px",
                    marginTop: "2px",
                    marginLeft: "4px",
                  }}
                >
                  All Day
                </div>
              )}
            </div>
          );
        },
      }}
      eventPropGetter={(event) => {
        let backgroundColor = "#D6E4FF";

        if (event.resource?.type === "APPOINTMENT") {
          backgroundColor = "#FFEDD5";
        } else if (event.resource?.type === "SESSION_BOOKING") {
          backgroundColor = "#DCFCE7";
        } else if (event.resource?.type === UNAVAILABLE_DAYS.PRACTITIONER_BOOKINGS) {
          backgroundColor = "#FEE2E2";
        }

        return {
          style: {
            backgroundColor,
            color: "#000",
            borderRadius: "6px",
            border: "1px solid #ccc",
            padding: "2px 4px",
          },
        };
      }}
      dayPropGetter={dayPropGetter}
    />
    </>
  );
};

export default PatientCalendar;